import type { NcFile } from '@api/domain/types';
import type { NCFileSearchItem, OperationSearchItem, ProductionBatch } from '@interfaces/search';
import type { SVGIconProps } from '@styles/icons/svg';
import { WithColor } from '@styles/colors';

export type TraceabilityNcFile = NCFileSearchItem | NcFile;

export enum TraceabilityNodeType {
  PART = 'part',
  PRODUCTION_ORDER = 'productionOrder',
  JOB = 'job',
  BATCH = 'batch',
  MACHINE = 'machine',
  NC_FILE = 'ncFile',
  VERSIONS = 'versions',
  DOCUMENTS = 'documents',
  EVENTS = 'events',
  SUMMARY = 'summary',
}

export interface TraceabilityEntitySummary {
  id: number;
  title: string;
  subtitle?: string | null;
  imageHash?: string | null;
  imageDocumentId?: number | null;
  ncFileStatus?: string | null;
}

export interface TraceabilityNode extends TraceabilityEntitySummary {
  nodeId: string;
  type: TraceabilityNodeType;
  eyebrow: string;
  icon: SVGIconProps['icon'];
  disabled?: boolean;
  onPress?: () => void;
}

export interface TraceabilityColumn {
  id: string;
  title: string;
  nodes: TraceabilityNode[];
  loading?: boolean;
}

// Measured vertical center (px, relative to the cards row) of each card, keyed by nodeId.
export type TraceabilityCardCenters = Record<string, number>;

// A single bezier connector between a source card and a target card in the next column.
export interface TraceabilityConnector {
  id: string;
  path: string;
}

export interface TraceabilityGraphActions {
  onOpenPart?: (entity: TraceabilityEntitySummary) => void;
  onOpenProductionOrder?: (entity: TraceabilityEntitySummary) => void;
  onOpenJob?: (entity: TraceabilityEntitySummary) => void;
  onOpenBatch?: (entity: TraceabilityEntitySummary) => void;
  onOpenNcFileSubScreen?: (subScreen: string) => void;
}

export interface TraceabilityGraphProps extends WithColor, TraceabilityGraphActions {
  ncFile: TraceabilityNcFile | null;
  ncFileLoaded: boolean;
  jobs: OperationSearchItem[];
  jobsLoaded: boolean;
  batches: ProductionBatch[];
  batchesLoading: boolean;
  batchesTotalCount: number | null;
  documentCount: number | null;
  documentsLoading: boolean;
}

// Item type of the ncFile.links array (derived so it follows the DTO if it changes).
export type LinkItem = NonNullable<TraceabilityGraphProps['ncFile']>['links'] extends Array<infer T> | null | undefined ? T : never;

// Item type of a job's machines array.
export type MachineItem = NonNullable<TraceabilityGraphProps['jobs'][number]['machines']> extends Array<infer T> ? T : never;

// Run-machine fields live on the by-id NcFile detail DTO but not on the by-name search projection.
export type NcFileMachineRun = {
  firstRanByMachineId?: number | null;
  firstRanByMachineName?: string | null;
  lastRanByMachineId?: number | null;
  lastRanByMachineName?: string | null;
};
